import React, { useState } from "react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  signOut,
  updateProfile,
} from "firebase/auth";
import app from "../../firebase.js";

function FinanceRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth(app);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] =
    useState("");
  const [showPassword, setShowPassword] =
    useState(false);
  const [acceptedTerms, setAcceptedTerms] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationCompleted, setRegistrationCompleted] =
    useState(false);
  const [message, setMessage] = useState("");

  const requestedReturnTo =
    location.state?.returnTo ||
    "/consulting/finance";

  const returnTo = requestedReturnTo.startsWith(
    "/consulting/finance"
  )
    ? requestedReturnTo
    : "/consulting/finance";

  const validatePassword = () => {
    const hasMinimumLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);

    return (
      hasMinimumLength &&
      hasUppercase &&
      hasLowercase &&
      hasNumber
    );
  };

  const handleRegistration = async (event) => {
    event.preventDefault();

    setMessage("");

    if (fullName.trim().length < 3) {
      setMessage(
        "Renseignez votre prénom et votre nom."
      );
      return;
    }

    if (!validatePassword()) {
      setMessage(
        "Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre."
      );
      return;
    }

    if (password !== passwordConfirmation) {
      setMessage(
        "Les deux mots de passe ne correspondent pas."
      );
      return;
    }

    if (!acceptedTerms) {
      setMessage(
        "Vous devez accepter les conditions d’utilisation et la politique de confidentialité."
      );
      return;
    }

    setLoading(true);

    try {
      const userCredential =
        await createUserWithEmailAndPassword(
          auth,
          email.trim(),
          password
        );

      await updateProfile(userCredential.user, {
        displayName: fullName.trim(),
      });

      await sendEmailVerification(
        userCredential.user
      );

      await signOut(auth);

      setRegistrationCompleted(true);
      setMessage("");
    } catch (error) {
      console.error(error);

      if (error.code === "auth/email-already-in-use") {
        setMessage(
          "Un compte existe déjà avec cette adresse e-mail."
        );
      } else if (error.code === "auth/invalid-email") {
        setMessage(
          "L’adresse e-mail renseignée n’est pas valide."
        );
      } else if (error.code === "auth/weak-password") {
        setMessage(
          "Le mot de passe choisi n’est pas suffisamment sécurisé."
        );
      } else {
        setMessage(
          "Impossible de créer le compte. Veuillez réessayer."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (registrationCompleted) {
    return (
      <div style={styles.page}>
        <div style={styles.backgroundCircleOne} />
        <div style={styles.backgroundCircleTwo} />

        <main style={styles.successCard}>
          <div style={styles.successIcon}>📩</div>

          <h1 style={styles.successTitle}>
            Vérifiez votre adresse e-mail
          </h1>

          <p style={styles.successText}>
            Votre compte HDC Finance a été créé. Un
            e-mail de vérification a été envoyé à :
          </p>

          <strong style={styles.successEmail}>
            {email}
          </strong>

          <p style={styles.successInformation}>
            Cliquez sur le lien reçu avant de vous
            connecter. Pensez également à vérifier vos
            courriers indésirables.
          </p>

          <button
            type="button"
            style={styles.primaryButton}
            onClick={() =>
              navigate(
                "/consulting/finance/connexion",
                {
                  replace: true,
                  state: { returnTo },
                }
              )
            }
          >
            Aller à la connexion →
          </button>

          <button
            type="button"
            style={styles.homeButton}
            onClick={() =>
              navigate("/consulting/finance")
            }
          >
            Retour à HDC Finance
          </button>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <button
        type="button"
        style={styles.backButton}
        onClick={() =>
          navigate("/consulting/finance")
        }
      >
        ← Retour à HDC Finance
      </button>

      <div style={styles.backgroundCircleOne} />
      <div style={styles.backgroundCircleTwo} />

      <main style={styles.card}>
        <div style={styles.brandIcon}>H</div>

        <div style={styles.badge}>
          ✨ Créer votre espace
        </div>

        <h1 style={styles.title}>
          Inscription HDC Finance
        </h1>

        <p style={styles.subtitle}>
          Créez un compte sécurisé pour déposer et suivre
          vos demandes.
        </p>

        <form
          style={styles.form}
          onSubmit={handleRegistration}
        >
          <FormField
            label="Prénom et nom"
            type="text"
            value={fullName}
            onChange={setFullName}
            placeholder="Votre identité complète"
            autoComplete="name"
          />

          <FormField
            label="Adresse e-mail"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="exemple@email.com"
            autoComplete="email"
          />

          <label style={styles.field}>
            <span style={styles.label}>
              Mot de passe
            </span>

            <div style={styles.passwordWrapper}>
              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                value={password}
                onChange={(event) =>
                  setPassword(event.target.value)
                }
                placeholder="Créez un mot de passe"
                autoComplete="new-password"
                style={styles.passwordInput}
                required
              />

              <button
                type="button"
                style={styles.eyeButton}
                onClick={() =>
                  setShowPassword(
                    (currentValue) =>
                      !currentValue
                  )
                }
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
          </label>

          <FormField
            label="Confirmer le mot de passe"
            type={showPassword ? "text" : "password"}
            value={passwordConfirmation}
            onChange={setPasswordConfirmation}
            placeholder="Saisissez-le à nouveau"
            autoComplete="new-password"
          />

          <div style={styles.passwordRules}>
            <PasswordRule
              valid={password.length >= 8}
              text="Au moins 8 caractères"
            />

            <PasswordRule
              valid={
                /[A-Z]/.test(password) &&
                /[a-z]/.test(password)
              }
              text="Une majuscule et une minuscule"
            />

            <PasswordRule
              valid={/\d/.test(password)}
              text="Au moins un chiffre"
            />
          </div>

          <label style={styles.termsLabel}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) =>
                setAcceptedTerms(event.target.checked)
              }
              style={styles.checkbox}
            />

            <span style={styles.termsText}>
              J’accepte les conditions d’utilisation et
              la politique de confidentialité de HDC
              Finance.
            </span>
          </label>

          {message && (
            <div style={styles.errorMessage}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.registerButton,
              ...(loading
                ? styles.disabledButton
                : {}),
            }}
          >
            {loading
              ? "Création du compte…"
              : "Créer mon compte →"}
          </button>
        </form>

        <div style={styles.separator}>
          <span style={styles.separatorLine} />

          <span style={styles.separatorText}>
            Vous avez déjà un compte ?
          </span>

          <span style={styles.separatorLine} />
        </div>

        <button
          type="button"
          style={styles.loginButton}
          onClick={() =>
            navigate(
              "/consulting/finance/connexion",
              {
                state: { returnTo },
              }
            )
          }
        >
          Se connecter
        </button>

        <p style={styles.securityText}>
          🔐 Votre mot de passe est géré de manière
          sécurisée par Firebase Authentication.
        </p>
      </main>
    </div>
  );
}

function FormField({
  label,
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
}) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>

      <input
        type={type}
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={styles.input}
        required
      />
    </label>
  );
}

function PasswordRule({ valid, text }) {
  return (
    <div
      style={{
        ...styles.passwordRule,
        color: valid ? "#86efac" : "#94a3b8",
      }}
    >
      <span>{valid ? "✓" : "○"}</span>
      <span>{text}</span>
    </div>
  );
}

const styles = {
  page: {
    position: "relative",
    minHeight: "100vh",
    padding: "70px 24px",
    boxSizing: "border-box",
    background:
      "linear-gradient(145deg, #050816 0%, #0b1020 60%, #111936 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  backButton: {
    position: "absolute",
    top: "30px",
    left: "30px",
    zIndex: 3,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#cbd5e1",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  backgroundCircleOne: {
    position: "absolute",
    top: "-160px",
    right: "-130px",
    width: "430px",
    height: "430px",
    borderRadius: "50%",
    background: "rgba(124, 58, 237, 0.18)",
    filter: "blur(20px)",
  },

  backgroundCircleTwo: {
    position: "absolute",
    bottom: "-180px",
    left: "-130px",
    width: "420px",
    height: "420px",
    borderRadius: "50%",
    background: "rgba(37, 99, 235, 0.15)",
    filter: "blur(20px)",
  },

  card: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: "480px",
    padding: "34px",
    boxSizing: "border-box",
    border:
      "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "26px",
    background: "rgba(255, 255, 255, 0.08)",
    boxShadow:
      "0 30px 80px rgba(0, 0, 0, 0.35)",
    backdropFilter: "blur(20px)",
    color: "#ffffff",
    textAlign: "center",
  },

  brandIcon: {
    width: "52px",
    height: "52px",
    margin: "0 auto",
    borderRadius: "16px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: "20px",
    fontWeight: "900",
  },

  badge: {
    display: "inline-flex",
    marginTop: "18px",
    padding: "7px 11px",
    borderRadius: "999px",
    background: "rgba(124, 58, 237, 0.15)",
    color: "#c4b5fd",
    fontSize: "10px",
    fontWeight: "900",
  },

  title: {
    margin: "18px 0 0",
    color: "#ffffff",
    fontSize: "29px",
    lineHeight: 1.15,
    fontWeight: "900",
    letterSpacing: "-0.03em",
  },

  subtitle: {
    margin: "12px auto 0",
    maxWidth: "380px",
    color: "#94a3b8",
    fontSize: "13px",
    lineHeight: 1.7,
  },

  form: {
    marginTop: "25px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    textAlign: "left",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
  },

  label: {
    color: "#cbd5e1",
    fontSize: "11px",
    fontWeight: "800",
  },

  input: {
    width: "100%",
    minHeight: "47px",
    padding: "0 14px",
    boxSizing: "border-box",
    border:
      "1px solid rgba(255, 255, 255, 0.14)",
    borderRadius: "12px",
    outlineColor: "#7c3aed",
    background: "rgba(2, 6, 23, 0.55)",
    color: "#ffffff",
    fontFamily: "inherit",
    fontSize: "13px",
  },

  passwordWrapper: {
    position: "relative",
  },

  passwordInput: {
    width: "100%",
    minHeight: "47px",
    padding: "0 48px 0 14px",
    boxSizing: "border-box",
    border:
      "1px solid rgba(255, 255, 255, 0.14)",
    borderRadius: "12px",
    outlineColor: "#7c3aed",
    background: "rgba(2, 6, 23, 0.55)",
    color: "#ffffff",
    fontFamily: "inherit",
    fontSize: "13px",
  },

  eyeButton: {
    position: "absolute",
    top: "50%",
    right: "13px",
    transform: "translateY(-50%)",
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
  },

  passwordRules: {
    padding: "12px",
    borderRadius: "11px",
    background: "rgba(2, 6, 23, 0.4)",
    display: "grid",
    gap: "6px",
  },

  passwordRule: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "10px",
    fontWeight: "700",
  },

  termsLabel: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    cursor: "pointer",
  },

  checkbox: {
    width: "17px",
    height: "17px",
    marginTop: "2px",
    accentColor: "#7c3aed",
  },

  termsText: {
    color: "#94a3b8",
    fontSize: "10px",
    lineHeight: 1.6,
  },

  errorMessage: {
    padding: "12px",
    border:
      "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "11px",
    background: "rgba(239, 68, 68, 0.12)",
    color: "#fca5a5",
    fontSize: "11px",
    lineHeight: 1.6,
  },

  registerButton: {
    minHeight: "48px",
    border: "none",
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
    cursor: "pointer",
    boxShadow:
      "0 12px 25px rgba(37, 99, 235, 0.24)",
  },

  disabledButton: {
    opacity: 0.65,
    cursor: "not-allowed",
  },

  separator: {
    marginTop: "24px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  separatorLine: {
    flex: 1,
    height: "1px",
    background:
      "rgba(255, 255, 255, 0.1)",
  },

  separatorText: {
    color: "#64748b",
    fontSize: "10px",
  },

  loginButton: {
    width: "100%",
    minHeight: "45px",
    marginTop: "18px",
    border:
      "1px solid rgba(167, 139, 250, 0.35)",
    borderRadius: "12px",
    background: "rgba(124, 58, 237, 0.08)",
    color: "#c4b5fd",
    fontSize: "12px",
    fontWeight: "900",
    cursor: "pointer",
  },

  securityText: {
    margin: "20px 0 0",
    color: "#64748b",
    fontSize: "10px",
    lineHeight: 1.6,
  },

  successCard: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: "470px",
    padding: "40px",
    boxSizing: "border-box",
    border:
      "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "26px",
    background: "rgba(255, 255, 255, 0.08)",
    color: "#ffffff",
    textAlign: "center",
    boxShadow:
      "0 30px 80px rgba(0, 0, 0, 0.35)",
    backdropFilter: "blur(20px)",
  },

  successIcon: {
    fontSize: "45px",
  },

  successTitle: {
    margin: "20px 0 0",
    fontSize: "29px",
    fontWeight: "900",
  },

  successText: {
    margin: "16px 0 0",
    color: "#cbd5e1",
    fontSize: "13px",
    lineHeight: 1.7,
  },

  successEmail: {
    display: "block",
    marginTop: "12px",
    color: "#c4b5fd",
    fontSize: "14px",
  },

  successInformation: {
    margin: "18px 0 0",
    color: "#94a3b8",
    fontSize: "11px",
    lineHeight: 1.7,
  },

  primaryButton: {
    width: "100%",
    minHeight: "47px",
    marginTop: "25px",
    border: "none",
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "900",
    cursor: "pointer",
  },

  homeButton: {
    width: "100%",
    minHeight: "43px",
    marginTop: "12px",
    border:
      "1px solid rgba(255, 255, 255, 0.14)",
    borderRadius: "11px",
    background: "transparent",
    color: "#cbd5e1",
    fontSize: "11px",
    fontWeight: "800",
    cursor: "pointer",
  },
};

export default FinanceRegister;